import { type ReactElement } from 'react';
import {
  TRANSITIONS,
  lueckeSchliessen,
  lueckenAufBasis,
  uebergangsStellen,
  uebergangsZiel,
  type Op,
  type TransitionKind,
} from '@schnitt/shared';
import { useEditor } from '../store';
import { useZeitText } from '../zeit';
import { Sheet } from './Sheet';

/**
 * Das Blatt „Übergänge“ (7.74a).
 *
 * Sascha, 8. September: „Für was haben wir den Übergänge Knopf? Er sollte
 * vielleicht auch mal funktionieren 🙂 … Mit visuellem Beispiel.“ Bis heute
 * war er ein Fach für später; der Übergang stand nur im Bearbeiten-Blatt
 * unter „Übergang zum vorigen Clip“, als Wortliste.
 *
 * Hier steht je Art eine Kachel, in der zwei Farbflächen den Übergang in
 * einer Schleife vorführen -- als CSS-Animation, nicht als Video: kein
 * Dekoder, und es sieht auf jedem Gerät gleich aus. Tippen setzt ihn an den
 * Schnitt, den `uebergangsZiel` bestimmt: der gewählte Clip, sonst der
 * Schnitt unter dem Kopf. Gibt es keinen, sagt das Blatt, was zu tun ist,
 * statt einen Übergang irgendwohin zu setzen.
 */

/** Die Namen, wie sie auf der Kachel stehen. */
const NAMEN: Record<TransitionKind, string> = {
  crossfade: 'Blende',
  dipToBlack: 'Über Schwarz',
  dipToWhite: 'Über Weiss',
  wipeleft: 'Wischen links',
  wiperight: 'Wischen rechts',
  wipeup: 'Wischen hoch',
  wipedown: 'Wischen runter',
  slideleft: 'Schieben links',
  slideright: 'Schieben rechts',
  circleopen: 'Kreis auf',
  circleclose: 'Kreis zu',
  horzopen: 'Balken quer',
  vertopen: 'Balken hoch',
  diagbr: 'Diagonal von oben links',
  diagtl: 'Diagonal von unten rechts',
  radial: 'Uhrzeiger',
  pixelize: 'Pixel',
};

export function UebergangSheet({ onClose }: { onClose: () => void }): ReactElement {
  const zeit = useZeitText();
  const timeline = useEditor((s) => s.timeline);
  const playhead = useEditor((s) => s.playhead);
  const selection = useEditor((s) => s.selection);
  const dispatch = useEditor((s) => s.dispatch);
  const setPlayhead = useEditor((s) => s.setPlayhead);
  const select = useEditor((s) => s.select);
  const toast = useEditor((s) => s.toast);
  const role = useEditor((s) => s.role);

  // 7.74d: Die Schnitte stehen im Blatt, statt getroffen werden zu müssen.
  // Ist keiner gewählt und steht der Kopf nirgends, nimmt das Blatt den
  // ersten -- ein Blatt, das „geht nicht“ sagt, obwohl es Schnitte gibt, war
  // Saschas Fund vom 9. September („ist irgendwie unverständlich“).
  const stellen = uebergangsStellen(timeline);
  const genau = uebergangsZiel(timeline, playhead, selection);
  // **Jeder Schnitt kann einen Übergang tragen** (18. September). Sascha:
  // „jedes andere schnittprogramm kann das auch überall / Mit den
  // Übergängen“. Bis dahin ging es nur auf der unteren Bildspur, und das Blatt
  // erklärte statt zu können -- samt einem Knopf, der beide Clips nach unten
  // legte. Der Umweg ist weg; oben wird jetzt genauso gerechnet.
  const gewaehlt = stellen.find((s) => selection.includes(s.clipId));
  const stelle = gewaehlt ?? (genau ? stellen.find((s) => s.clipId === genau.clipId) : undefined) ?? stellen[0];
  const ziel = stelle ? { clipId: stelle.clipId, bei: stelle.bei, label: stelle.label } : null;
  const clip = ziel ? timeline.tracks.flatMap((t) => t.clips).find((c) => c.id === ziel.clipId) : undefined;
  const aktuell = clip?.transitionIn ?? null;
  const fps = timeline.settings.fps;

  /**
   * Die Lücke, an der es scheitert (7.105).
   *
   * Sascha, 17. September: „Will ich ein Übergang hinzufügen zwischen zwei
   * auseinander gecutteten Tracks, kann ich keinen auswählen. Nur harter
   * Schnitt bleibt aktiv.“ Zwischen den beiden lag eine Lücke, und dort gibt
   * es nichts zu überblenden -- richtig, aber das Blatt riet „zieh sie
   * zusammen“ und liess die Arbeit bei ihm.
   *
   * Genommen wird die Lücke am Abspielkopf, sonst die erste.
   */
  const luecken = ziel || role === 'viewer' ? [] : lueckenAufBasis(timeline);
  const luecke =
    luecken.find((l) => selection.includes(l.clipId)) ??
    luecken
      .map((l) => ({ l, weg: Math.abs((timeline.tracks.find((t) => t.id === l.trackId)?.clips.find((c) => c.id === l.clipId)?.start ?? 0) - playhead) }))
      .sort((a, b) => a.weg - b.weg)[0]?.l ??
    null;

  const schliesseLuecke = () => {
    if (!luecke) return;
    const spur = timeline.tracks.find((t) => t.id === luecke.trackId);
    if (!spur) return;
    const zug = lueckeSchliessen(spur.clips, luecke.clipId);
    if (zug.length === 0) return;
    dispatch(zug.map((z) => ({ op: 'moveClip', clipId: z.clipId, toTrackId: luecke.trackId, start: z.start })) as Op[]);
    select([luecke.clipId]);
    toast('success', 'Lücke geschlossen. Jetzt geht der Übergang.');
  };

  const setze = (kind: TransitionKind | null) => {
    if (!ziel || !clip) return;
    const transition = kind ? { kind, duration: aktuell?.duration ?? 0.5 } : null;
    dispatch({ op: 'setTransition', clipId: clip.id, transition } as Op);
    // Den Kopf an den Schnitt, damit man sieht, was man gesetzt hat.
    setPlayhead(Math.max(0, ziel.bei - (transition?.duration ?? 0) / 2));
    toast('success', kind ? `${NAMEN[kind]} gesetzt.` : 'Harter Schnitt.');
  };

  return (
    <Sheet title="Übergänge" onClose={onClose}>
      {/* Welcher Schnitt: bei mehreren zum Wählen, bei einem als Auskunft. */}
      {stellen.length > 1 ? (
        <div className="chips" data-uebergang-stellen>
          {stellen.map((stelle) => (
            <button
              key={stelle.clipId}
              className={`chip ${stelle.clipId === (gewaehlt?.clipId ?? ziel?.clipId) ? 'active' : ''}${stelle.aufBasis ? '' : ' fremd'}`}
              data-uebergang-stelle={stelle.clipId}
              title={stelle.aufBasis ? 'Schnitt auf der unteren Bildspur' : `Schnitt auf der Spur ${stelle.spurName}`}
              onClick={() => {
                select([stelle.clipId]);
                setPlayhead(stelle.bei);
              }}
            >
              {zeit(stelle.bei)}
              {stelle.transition ? ' ·' : ''}
            </button>
          ))}
        </div>
      ) : null}
      {ziel && clip ? (
        <p className="hint" style={{ marginTop: 0 }} data-uebergang-ziel={clip.id}>
          Am Schnitt bei {zeit(ziel.bei)}, zwischen dem Clip davor und <b>„{ziel.label}“</b>
          {stelle && !stelle.aufBasis ? <> auf der Spur <b>„{stelle.spurName}“</b></> : null}.
          {aktuell ? ` Jetzt: ${NAMEN[aktuell.kind]}, ${aktuell.duration.toFixed(2)} s.` : ' Jetzt: harter Schnitt.'}
        </p>
      ) : luecke ? (
        <>
          <p className="hint" style={{ marginTop: 0 }} data-uebergang-ziel="" data-uebergang-luecke={luecke.clipId}>
            Zwischen <b>„{luecke.vorLabel}“</b> und <b>„{luecke.label}“</b> liegt eine Lücke von{' '}
            {luecke.breite.toFixed(2)} s. Ein Übergang braucht einen Schnitt — dort ist keiner.
          </p>
          <button className="primary" style={{ width: '100%', marginBottom: 12 }} data-luecke-schliessen onClick={schliesseLuecke}>
            Lücke schliessen
          </button>
        </>
      ) : (
        <p className="hint" style={{ marginTop: 0 }} data-uebergang-ziel="">
          Ein Übergang braucht zwei Clips, die auf der Basisspur aneinander liegen. Zieh sie zusammen — an der Kante
          zwischen ihnen erscheint dann ein <b>+</b>, und das führt hierher.
        </p>
      )}
      <div className="uebergang-kacheln">
        <button
          /* Ohne Ziel ist nichts gewählt -- „aktiv“ hiesse sonst, der harte
             Schnitt sei die einzige Möglichkeit, und genau so hat Sascha es
             gelesen (7.105). */
          className={`uebergang-kachel ${ziel && !aktuell ? 'active' : ''}`}
          data-art="hart"
          disabled={!ziel}
          onClick={() => setze(null)}
        >
          <span className="uebergang-bild" aria-hidden="true">
            <i className="a" />
            <i className="b" />
          </span>
          <span>Harter Schnitt</span>
        </button>
        {TRANSITIONS.map((kind) => (
          <button
            key={kind}
            className={`uebergang-kachel ${aktuell?.kind === kind ? 'active' : ''}`}
            data-art={kind}
            disabled={!ziel}
            onClick={() => setze(kind)}
          >
            <span className="uebergang-bild" aria-hidden="true">
              <i className="a" />
              <i className="b" />
            </span>
            <span>{NAMEN[kind]}</span>
          </button>
        ))}
      </div>
      {aktuell && clip ? (
        /* 7.74g: Und wieder weg. Sascha, 11. September: „Wie mache ich einen
           Übergang wieder weg?“ Er musste fragen -- die Antwort stand als
           erste Kachel unter „Harter Schnitt“, dem Fachwort für das, was
           danach dasteht. Wer etwas wegnehmen will, sucht nach „weg“. Also
           steht es jetzt dort, wo man hinsieht, wenn einer sitzt: neben der
           Dauer, in Worten. Die Kachel bleibt -- sie sagt, was daraus wird. */
        <button className="uebergang-weg" data-uebergang-weg={clip.id} onClick={() => setze(null)}>
          Übergang entfernen
        </button>
      ) : null}
      {aktuell && clip ? (
        <label className="uebergang-dauer">
          <span>Dauer {aktuell.duration.toFixed(2)} s</span>
          <input
            type="range"
            min={0.1}
            max={Math.min(3, clip.duration)}
            step={0.05}
            value={aktuell.duration}
            onChange={(e) =>
              dispatch({ op: 'setTransition', clipId: clip.id, transition: { kind: aktuell.kind, duration: Number(e.target.value) } } as Op)
            }
          />
        </label>
      ) : null}
      <p className="hint">
        Ein Übergang überlappt mit dem vorigen Clip. Reicht dessen Material nicht, wird er gekürzt. Die Vorschau zeigt
        jede Art als Blende; der Export rechnet die echte.
      </p>
    </Sheet>
  );
}
